import { useState, useRef, useEffect, useContext} from "react";
import { Resizable } from "re-resizable";
import "../../../../styles/Widgets/GeneralWidget.css";
import "../../../../styles/Widgets/Charts/BarChartWidget.css";
import { useParams } from "react-router-dom";
import { thingsBoardMicroserviceUrl } from "../../../../microservices/thingsboard_microservice";
import JwtDecodeService from "../../../../services/JwtDecodeService";
import { AppContext } from "../../../AppProvider";
import TimeWindowSelector, { getQuickIntervalByValue, getTimeIntervalByValue } from "../../../controls/TimeWindowSelector";
import { ResponsiveBar } from "@nivo/bar";
import TelemetryToCSV from "../../../../functions/TelemetryToCSV";
import { LoadShortDate } from "../../../../functions/LoadShortDate";
import TelemetryToExcel from "../../../../functions/TelemetryToExcel";
import { TIME_WINDOW_BUCKETS } from "./javascript/TimeWindowBuckets";
import { getTickIntervalAndFormat } from "./javascript/TickIntervalAndTimeFormat";


const BarChartWidget = ({DashboardContext, widget, setWidgets, bounds, setDraggingOrResizing, MOBILE_COMPUTER_SCREEN_BOUNDARY }) => {
    const {
        editMode,setActiveDashboardState,
        activeEditWidget,setActiveEditWidget,
        maximizedWidget,setMaximizedWidget
    } = useContext(DashboardContext);
    const {user,customerIotProfile,logout} = useContext(AppContext);
    const {tenantId,customerAdminId,customerUserId} = useParams();

    //region Bar chart widget variables
    const [timeWindow,setTimeWindow] = useState(widget?.config?.timeWindow);
    const [showTimeWindowSelector,setShowTimeWindowSelector] = useState(false);
    const [loadingData,setLoadingData] = useState(false);
    const [telemetryHistory,setTelemetryHistory] = useState(null);
    const [serverTime,setServerTime] = useState(null);
    const [showExportOptions,setShowExportOptions] = useState(false);
    const dateTimePickerOpen = useRef(false);
    const timeWindowSelectorContainerRef = useRef(null);
    const exportButtonRef = useRef(null);
    const MAX_VALUE_KEY = '293e4567-e89b-12d3-a456-426614174649_#_max_value_!'
    //End region
    
    const fetchRealTimeTelemetryHistory = async(deviceId) =>{;
        try{
            if(!telemetryHistory) setLoadingData(true);
            const interval = timeWindow?.selectedTab === 0? timeWindow?.realTime?.interval:timeWindow?.history?.interval;
            const timeWindowMs = timeWindow?.selectedTab === 0? timeWindow?.realTime?.timeWindowMs:timeWindow?.history?.timeWindowMs;
            const specificTelemetryHistory = timeWindow?.selectedTab === 1 && timeWindow?.history?.historyType === 1 && timeWindow?.history?.fixedTimeWindow;
            const specificStartTimeMs = specificTelemetryHistory? timeWindow?.history?.fixedTimeWindow?.startTimeMs:null;
            const specificEndTimeMs = specificTelemetryHistory? timeWindow?.history?.fixedTimeWindow?.endTimeMs:null;
            const aggregation = timeWindow?.aggregation?.type; 
            const telemetryKeys = widget?.config?.series?.map(seriesEntry => seriesEntry.key).join(',');
            const decodedAccessToken = JwtDecodeService(user?.access_token);
            const defaultAuthority = decodedAccessToken && decodedAccessToken["user-authorities"].length > 0? decodedAccessToken["user-authorities"][0] : 'NONE';
            let url;
            if(specificTelemetryHistory && Number.isFinite(specificStartTimeMs) && Number.isFinite(specificEndTimeMs)){
                if(defaultAuthority === 'TENANT_ADMIN') url = `${thingsBoardMicroserviceUrl}/tenants/profiles/${tenantId}/devices/${deviceId}/telemetry/history/specific?interval=${interval}&aggregation=${aggregation}&startTimeMs=${specificStartTimeMs}&endTimeMs=${specificEndTimeMs}&keys=${encodeURIComponent(telemetryKeys)}`;
                else if(defaultAuthority === 'CUSTOMER' && customerIotProfile?.role === 'ADMIN') url = `${thingsBoardMicroserviceUrl}/customers/users/admin/${customerAdminId}/devices/${deviceId}/telemetry/history/specific?interval=${interval}&aggregation=${aggregation}&startTimeMs=${specificStartTimeMs}&endTimeMs=${specificEndTimeMs}&&keys=${encodeURIComponent(telemetryKeys)}`;
                else if(defaultAuthority === 'CUSTOMER' && customerIotProfile?.role === 'USER') url = `${thingsBoardMicroserviceUrl}/customers/users/user/${customerUserId}/devices/${deviceId}/telemetry/history/specific?interval=${interval}&aggregation=${aggregation}&startTimeMs=${specificStartTimeMs}&endTimeMs=${specificEndTimeMs}&&keys=${encodeURIComponent(telemetryKeys)}`;
            }
            else{
                if(defaultAuthority === 'TENANT_ADMIN') url = `${thingsBoardMicroserviceUrl}/tenants/profiles/${tenantId}/devices/${deviceId}/telemetry/history?interval=${interval}&aggregation=${aggregation}&timeWindowMs=${timeWindowMs}&keys=${encodeURIComponent(telemetryKeys)}`;
                else if(defaultAuthority === 'CUSTOMER' && customerIotProfile?.role === 'ADMIN') url = `${thingsBoardMicroserviceUrl}/customers/users/admin/${customerAdminId}/devices/${deviceId}/telemetry/history?interval=${interval}&aggregation=${aggregation}&timeWindowMs=${timeWindowMs}&keys=${encodeURIComponent(telemetryKeys)}`;
                else if(defaultAuthority === 'CUSTOMER' && customerIotProfile?.role === 'USER') url = `${thingsBoardMicroserviceUrl}/customers/users/user/${customerUserId}/devices/${deviceId}/telemetry/history?interval=${interval}&aggregation=${aggregation}&timeWindowMs=${timeWindowMs}&keys=${encodeURIComponent(telemetryKeys)}`;
            }
            const response = await fetch(url,{
                method:'GET',
                headers:{
                    'Authorization':`Bearer ${user.access_token}`,
                    'Content-Type':'application/json'
                }
            });
            if(response.ok){
                setTelemetryHistory(await response.json());
            }
            else if(Number(response.status) === 401) logout();
            else throw new Error(`Response code: ${response.status}`);
        }
        catch(error){
            console.error(`An error occurred when retrieving telemetry history for device with id ${deviceId}: `,error);
        }
        setLoadingData(false);
    };
    const fetchServerTime = async() =>{
        try{
            const url = `${thingsBoardMicroserviceUrl}/utilities/time/epoch`;
            const response = await fetch(url,{
                method:'GET',
                headers:{
                    'Authorization':`Bearer ${user.access_token}`,
                    'Content-Type':'application/json'
                }
            });
            if(response.ok){
                setServerTime(await response.json());
            }
            else if(Number(response.status) === 401) logout();
            else throw new Error(`Response code: ${response.status}`);
        }
        catch(error){
            console.error('An error occurred when retrieving server time',error);
        }
    };
    //region Widget General features
    const { gridCellSize } = bounds;
    const [resizing,setResizing] = useState(false);
    const [dragging,setDragging] = useState(false);
    const [newWidgetSize,setNewWidgetSize] = useState({ width: widget.sizeX, height: widget.sizeY });
    const [widgetPosition, setWidgetPosition] = useState({ col: widget.col, row: widget.row });
    const [widgetSize, setWidgetSize] = useState({ width: widget.sizeX, height: widget.sizeY });
    const resizableRef = useRef(null);
    const mouseMoveRef = useRef(null);
    const mouseUpRef = useRef(null);
    const position = { 
        x: widgetPosition.col * (gridCellSize + bounds.colGap) + bounds.colGap,
        y: widgetPosition.row * (gridCellSize + bounds.rowGap) + bounds.rowGap
    };
    const size = { 
        width: maximizedWidget && maximizedWidget?.widget?.id === widget?.id? window.innerWidth:widgetSize.width * gridCellSize,
        height: maximizedWidget && maximizedWidget?.widget?.id === widget?.id? window.innerHeight:widgetSize.height * gridCellSize
    };
    const screenIsMobileScreen = () =>{
        return bounds.width < MOBILE_COMPUTER_SCREEN_BOUNDARY;
    };
    const handleMouseDown = (e) => {
        if(!editMode || resizing || screenIsMobileScreen()) return;
        if(resizableRef.current?.resizableElement?.contains(e.target)) return;

        setDragging(true);
        const startX = e.clientX - position.x;
        const startY = e.clientY - position.y;

        const handleMouseMove = (e) => {
            let newCol = Math.round((e.clientX - startX) / gridCellSize);
            let newRow = Math.round((e.clientY - startY) / gridCellSize);

            newCol = Math.max(0, Math.min(Math.floor(bounds.width / gridCellSize) - widgetSize.width, newCol));
            newRow = Math.max(0, Math.min(Math.floor(bounds.height / gridCellSize) - widgetSize.height, newRow));

            setWidgetPosition({ col: newCol, row: newRow });
        };

        const handleMouseUp = () => {
            setDragging(false);
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };

        mouseMoveRef.current = handleMouseMove;
        mouseUpRef.current = handleMouseUp;
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };
    const handleResizing = (e,direction,ref) =>{
        const newWidth = ref.clientWidth;
        const newHeight = ref.clientHeight;
        setNewWidgetSize({
            width:newWidth,
            height:newHeight
        });
    };
    const exportWidget = () =>{
        const blob = new Blob([JSON.stringify(widget,null,2)],{type: "application/json"});
        const url = URL.createObjectURL(blob);
        const a = Object.assign(document.createElement("a"), {href: url, download: `${widget.type}_${widget?.appearance?.config?.title}.json`});
        a.click();
        URL.revokeObjectURL(url);
    };
    const removeWidget = () =>{
        setActiveDashboardState(prev =>({
            ...prev,widgets:prev?.widgets?.filter(w => w.id !== widget?.id)
        }));
    };
    //endregion
    const snapToNearestTimeWindowMs = ms =>
        TIME_WINDOW_BUCKETS.reduce((prev, curr) =>Math.abs(curr - ms) < Math.abs(prev - ms) ? curr : prev
    );
    const formatXAxis = () =>{
        let timeWindowMsRaw = timeWindow?.selectedTab === 0? timeWindow?.realTime?.timeWindowMs:
            timeWindow?.history?.historyType === 1? timeWindow?.history?.fixedTimeWindow?.endTimeMs - timeWindow?.history?.fixedTimeWindow?.startTimeMs:timeWindow?.history?.timeWindowMs;
        const timeWindowMs = snapToNearestTimeWindowMs(timeWindowMsRaw);
        const { tickIntervalMs, format } = getTickIntervalAndFormat(timeWindowMs,widget,MOBILE_COMPUTER_SCREEN_BOUNDARY,bounds);
        let minTs,maxTs;
        if(timeWindow.selectedTab === 1 && timeWindow.history.historyType === 1 && timeWindow.history.fixedTimeWindow){
            minTs = timeWindow.history.fixedTimeWindow.endTimeMs - timeWindowMs;
            maxTs = timeWindow.history.fixedTimeWindow.endTimeMs;
        }
        else{
            const now = Date.now();
            minTs = now - timeWindowMs;
            maxTs = now;
        }
        //const alignedStart = minTs + (tickIntervalMs - (minTs % tickIntervalMs)) % tickIntervalMs;
        const alignedStart = Math.floor(minTs / tickIntervalMs) * tickIntervalMs + tickIntervalMs / 2;
        const tickValues = [];
        for (let ts = alignedStart; ts <= maxTs; ts += tickIntervalMs) {
            tickValues.push(ts);
        }
        return { format, tickValues };
    };
    const calculateXAxisLeftMargin = () =>{
        return Math.max(0,...gappedFlattenedData?.flatMap(d => Object.entries(d).filter(([k]) => k !== 'ts').map(([,v]) => Math.abs(Number(v)).toFixed(2).length))) * 6;
    };
    useEffect(() =>{
        function handleClickOutsideButton(e){
            if(timeWindowSelectorContainerRef.current && !timeWindowSelectorContainerRef.current.contains(e.target) && !dateTimePickerOpen.current){
                setShowTimeWindowSelector(false);
            }
            if(exportButtonRef.current && !exportButtonRef.current.contains(e.target)){
                setShowExportOptions(false);
            }
        }
        document.addEventListener("click",handleClickOutsideButton);
        return () =>{
            document.removeEventListener("click",handleClickOutsideButton);
            window.removeEventListener("mouseup", mouseMoveRef.current);
            window.removeEventListener("mouseup",mouseUpRef.current);
        }
    },[]);
    useEffect(() =>{
        if(widget?.timeWindow) setTimeWindow(widget?.timeWindow);
        if(maximizedWidget?.widget?.id === widget?.id){
            setWidgetPosition({ col: 0, row: 0 });
        }
    },[widget]);
    useEffect(() =>{
        setWidgetSize(prev => ({
            width: Math.round(prev.width),
            height: Math.round(prev.height),
        }));
        setWidgetPosition(prev => ({
            col: Math.round(prev.col),
            row: Math.round(prev.row),
        }));
    },[gridCellSize]);
    useEffect(() =>{
        setDraggingOrResizing(dragging || resizing);
    },[resizing,dragging]);
    useEffect(() =>{
        if(screenIsMobileScreen()) return;
        setNewWidgetSize({
            width:widgetSize.width * gridCellSize,
            height:widgetSize.height * gridCellSize
        });
        setWidgets((prevWidgets) => prevWidgets.map((prevWidget) =>(
            prevWidget.id === widget.id? {
                ...prevWidget,
                row: widgetPosition.row,
                col: widgetPosition.col,
                sizeX: widgetSize.width,
                sizeY: widgetSize.height
            }: prevWidget
        )));
    },[widgetPosition,widgetSize]);
    useEffect(() =>{
        setTelemetryHistory(null);
    },[timeWindow]);
    useEffect(() =>{
        if(telemetryHistory != null) return;
        const interval = timeWindow?.selectedTab === 0? timeWindow?.realTime?.interval:timeWindow?.history?.interval;
        fetchRealTimeTelemetryHistory(widget?.config?.dataSource?.deviceId);
        fetchServerTime();
        const intervalId = setInterval(() => {
            fetchRealTimeTelemetryHistory(widget?.config?.dataSource?.deviceId);
            fetchServerTime();
        },interval);
        return () =>{
            clearInterval(intervalId);
        }
    },[telemetryHistory,timeWindow]);

    const CustomGridX = ({ width, height, margin}) => {
        const step = (width - (margin.right * 7)) / 9;
        const lines = [];

        for (let i = 1; i < 10; i++) {
            const x = step * i;
            lines.push(
                <line
                    key={i}
                    x1={x}
                    x2={x}
                    y1={0}
                    y2={height - margin.bottom * 1.62}
                    stroke="#aaa"
                    strokeWidth={0.5}
                />
            );
        }
        return <g>{lines}</g>;
    };
    const makeGapEntry = (ts,keys) =>{
        const gapEntry = { ts };
        keys.forEach(k => gapEntry[k] = null);
        return gapEntry;
    }
    const insertGaps = (data) =>{
        const refined = [];
        const interval = timeWindow?.selectedTab === 0? timeWindow?.realTime?.interval:timeWindow?.history?.interval;
        const timeWindowMs = timeWindow?.selectedTab === 0? timeWindow?.realTime?.timeWindowMs:timeWindow?.history?.timeWindowMs;
        if(!data || data.length === 0) return refined;
        const keys = Object.keys(data.find(entry => entry && typeof entry === 'object') || {}).filter(k => k !== 'ts');
        const windowStartTs = serverTime?.now && Number.isFinite(serverTime?.now)? serverTime?.now - timeWindowMs:null;
        let firstEntry = data[0];
        if(windowStartTs && Number.isFinite(firstEntry?.ts)){
            let ts = windowStartTs;
            while(ts + interval < firstEntry?.ts){
                refined.push(makeGapEntry(ts,keys));
                ts += interval;
            }
        }
        let lastEntry = data[0];
        refined.push(lastEntry);
        for(let i = 1;i < data.length; i++){
            const currentEntry = data[i];
            let lastTs = Number(lastEntry.ts);
            const currentTs = Number(currentEntry?.ts);
            if(!Number.isFinite(currentTs)) continue;

            while(lastTs + interval < currentTs){
                lastTs += interval;
                const gapEntry = {ts: lastTs};
                keys.forEach(k => gapEntry[k] = null);
                refined.push(gapEntry);
            }
            refined.push(currentEntry);
            lastEntry = currentEntry;
        }
        if(serverTime?.now && Number.isFinite(serverTime?.now)){
            let lastTs = Number(lastEntry.ts);
            while(lastTs + interval < serverTime?.now){
                lastTs += interval;
                const gapEntry = { ts: lastTs}
                keys.forEach(k => gapEntry[k] = null);
                refined.push(gapEntry);
            }
        }
        return refined;
    };
    const injectMaxValue = (flattenedData) =>{
        const maxValueEntry = {ts:flattenedData.length > 0? flattenedData[0]?.ts:Date.now(),[MAX_VALUE_KEY]:widget?.config?.appearance?.maximumValue || 0};
        return [...flattenedData,maxValueEntry];
    };
    const flattenedData = telemetryHistory && telemetryHistory.length > 0? Object.values(
        telemetryHistory.map(entry => ({
            ts: Number(entry.ts),
            ...Object.fromEntries(
                Object.entries(entry.values).map(([key, value]) => [key, Number(value)])
            )
        })).filter(entry => Number.isFinite(entry.ts))
        .reduce((acc, entry) => {
            acc[entry.ts] = { ...(acc[entry.ts] || { ts: entry.ts }), ...entry };
            return acc;
        }, {})
    ).sort((a, b) => a.ts - b.ts):[];//.slice(0, 1000):[];
    const gappedFlattenedData = injectMaxValue(insertGaps(flattenedData));
    return (
        <div className={`general-widget-parent-container ${editMode? (activeEditWidget?.id === widget?.id? 'selected-edit':activeEditWidget? 'active-edit':'passive-edit'):''}`}
            style={{
                position: "absolute",
                left: position.x,
                top: position.y,
                cursor: dragging ? "grabbing" : "default"
            }}
            onClick={() =>{
                if(!activeEditWidget) return;
                setActiveEditWidget(widget);
            }}
            onMouseDown={handleMouseDown}>
            <div className={`general-widget-active-edit-mask ${editMode && activeEditWidget && activeEditWidget?.id !== widget?.id? 'active':''}`}></div>
            <Resizable
                ref={resizableRef}
                size={size}
                minWidth={3 * gridCellSize}
                minHeight={3 * gridCellSize}
                enable={{ right: editMode && !activeEditWidget, bottom: editMode && !activeEditWidget, bottomRight: editMode && !activeEditWidget }}
                onResizeStart={(e) => {
                    e.stopPropagation();
                    setResizing(true);
                }}
                onResize={(e, direction, ref) =>handleResizing(e,direction,ref)}
                onResizeStop={(e,direction,ref) => {
                    setWidgetSize({
                        width: Math.round(ref.clientWidth / gridCellSize),
                        height: Math.round(ref.clientHeight / gridCellSize),
                    });
                    setResizing(false);
                }}>
                <div className="general-widget-container" style={{borderRadius:`${widget?.borderRadius}%`}} onMouseDown={handleMouseDown}>
                    <div className="general-widget-edit-controls-container"
                        style={{display:editMode && !activeEditWidget? 'flex':'none'}}>
                        <div onClick={() => setActiveEditWidget(widget)}>
                            <p className="edit-widget-p">&#9997;</p>
                        </div>
                        <div onClick={() => exportWidget()}>
                            <p className="export-widget-p">&#128229;</p>
                        </div>
                        <div onClick={() => removeWidget()}>
                            <p className="remove-widget-p">&#10006;</p>
                        </div>
                    </div>
                    <div className="bar-chart-widget-container">
                        <div className="bar-chart-widget-header">
                            <div className="details-header">
                                <p style={{
                                    visibility:widget?.config?.appearance?.showTitle? 'visible':'hidden',
                                    color:widget?.config?.appearance?.titleColor}}>
                                        {widget?.config?.appearance?.title}
                                </p>
                                <div className="control-buttons-container">
                                    <div className="export-button" title="Export widget data" ref={exportButtonRef}
                                        onClick={() =>{
                                            setShowExportOptions(!showExportOptions);
                                        }}>
                                        <img src={process.env.PUBLIC_URL + '/images/export-telemetry-icon.png'}/>
                                        <div className="export-selections-container"
                                            style={{display:showExportOptions? 'block':'none'}}
                                            onClick={() => setShowExportOptions(false)}>
                                            <button onClick={() => TelemetryToCSV(flattenedData,widget? `Bar chart ${widget?.config?.appearance?.title} ${LoadShortDate(new Date())} ts.csv`:null,false)}>Export to CSV - Ts</button>
                                            <button onClick={() => TelemetryToCSV(flattenedData,widget? `Bar chart ${widget?.config?.appearance?.title} ${LoadShortDate(new Date())} dt.csv`:null,true)}>Export to CSV - Dt</button>
                                            <button onClick={() => TelemetryToExcel(flattenedData,widget? `Bar chart ${widget?.config?.appearance?.title} ${LoadShortDate(new Date())} ts.xlsx`:null,false)}>Export to Excel - Ts</button>
                                            <button onClick={() => TelemetryToExcel(flattenedData,widget? `Bar chart ${widget?.config?.appearance?.title} ${LoadShortDate(new Date())} dt.xlsx`:null,true)}>Export to Excel - Dt</button>
                                        </div>
                                    </div>
                                    <button className="maximize-button" title="Maximize"
                                        style={{display:maximizedWidget && maximizedWidget?.widget?.id === widget?.id? 'none':'block'}}
                                        onClick={() => setMaximizedWidget({ 
                                            widget:widget, 
                                            setWidgets:setWidgets,
                                            bounds:bounds,
                                            setDraggingOrResizing:setDraggingOrResizing,
                                            MOBILE_COMPUTER_SCREEN_BOUNDARY:MOBILE_COMPUTER_SCREEN_BOUNDARY }
                                        )}>
                                        <img src={process.env.PUBLIC_URL + '/images/maximize-widget-icon.png'}/>
                                    </button>
                                    <button className="minimize-button" title="Minimize"
                                        style={{display:maximizedWidget && maximizedWidget?.widget?.id === widget?.id? 'block':'none'}}
                                        onClick={() => setMaximizedWidget(null)}>
                                        <img src={process.env.PUBLIC_URL + '/images/minimize-widget-icon.png'}/>
                                    </button>
                                </div>
                            </div>
                            <div ref={timeWindowSelectorContainerRef} className="time-window-selector-container"
                                style={{display:widget?.config?.displayTimeWindow? 'block':'none'}}>
                                <button
                                    className="show-time-window-selector-button"
                                    onClick={() => setShowTimeWindowSelector(!showTimeWindowSelector)}>
                                    <img src={`${process.env.PUBLIC_URL}/images/clock-icon.png`}/>
                                    {timeWindow?.selectedTab === 0? 'Real time':'History'} - {
                                        timeWindow?.selectedTab === 0?
                                        timeWindow?.realTime?.realTimeType === 0? `last ${getTimeIntervalByValue(timeWindow?.realTime?.timeWindowMs).replace(/^1 /, '')}`:getQuickIntervalByValue(timeWindow?.realTime?.quickInterval):
                                        timeWindow?.history?.historyType === 0? `last ${getTimeIntervalByValue(timeWindow?.history?.timeWindowMs).replace(/^1 /, '')}`:
                                        timeWindow?.history?.historyType === 1? `from ${(new Date(timeWindow?.history?.fixedTimeWindow?.startTimeMs))?.toLocaleString()} to ${(new Date(timeWindow?.history?.fixedTimeWindow?.endTimeMs))?.toLocaleString()}`
                                        :getQuickIntervalByValue(timeWindow?.history?.quickInterval)
                                    }
                                </button>
                                <TimeWindowSelector
                                    showTimeWindowSelector={showTimeWindowSelector}
                                    setShowTimeWindowSelector={setShowTimeWindowSelector}
                                    dateTimePickerOpen={dateTimePickerOpen}
                                    timeWindow={timeWindow}
                                    setTimeWindow={setTimeWindow}
                                />
                            </div>
                        </div>
                        <div className="bar-chart-widget-body">
                            <div className="legends-container">
                                {widget?.config?.series && widget?.config?.series.map((seriesEntry,index) =>(
                                    <div className="legend-container" key={index}>
                                        <div className="key-container">
                                            <p style={{
                                                width:'10px',height:'10px',
                                                borderRadius:'50%',
                                                backgroundColor:seriesEntry?.color
                                            }}></p>
                                            <p>{seriesEntry?.label}</p>
                                        </div>
                                        <div className="value-container">
                                            <p>{seriesEntry?.units?.symbol}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="graph-container">
                                <ResponsiveBar
                                    data={gappedFlattenedData}
                                    keys={[...widget?.config?.series?.map(s => s?.key),MAX_VALUE_KEY]}
                                    indexBy={'ts'}
                                    margin={{ top: 20, right: 5, bottom: 30, left: Math.max(0,...gappedFlattenedData?.flatMap(
                                        d => Object.entries(d).filter(([k]) => k !== 'ts').map(([,v]) => Math.abs(Number(v)).toFixed(2).length)
                                    )) * 6 }}
                                    padding={Math.min(0.92, Math.max(0.05, 20/ Math.pow(gappedFlattenedData.length,0.8)))}
                                    groupMode="grouped"
                                    colors={({ id }) => id === MAX_VALUE_KEY? 'transparent':widget?.config?.series?.find( s => s.key === id)?.color || undefined}
                                    axisLeft={{ tickSize: 5, tickPadding: 5 }}
                                    axisBottom={null}
                                    enableGridY={true}
                                    enableLabel={false}
                                    tooltip={({id,value,indexValue,color}) =>{
                                        const date = new Date(indexValue);
                                        const formattedDateTime = date.toLocaleString('en-KE', {
                                            weekday: 'short',
                                            day: 'numeric',
                                            month: 'short',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                            hour12: false
                                        });
                                        return(
                                            <div className="graph-tool-tip">
                                                <p className="date-time-p">{formattedDateTime}</p>
                                                <p className="value-p">
                                                    <span>
                                                        <span className="icon-span" style={{backgroundColor:color}}></span>
                                                        <span className="id-span">{id}</span>
                                                    </span>
                                                    <span>{value}</span>
                                                </p>
                                            </div>
                                        );
                                    }}
                                    layers={['grid', 'axes', 'bars', 'markers', 'legends', CustomGridX]}
                                    theme={{
                                        axis: {
                                            domain: {
                                                line: { stroke: '#000', strokeWidth: 1}
                                            },
                                            ticks: {
                                                line: { stroke: '#555',strokeWidth: 1},
                                                text: { fill: '#666', fontSize: 12},
                                            }
                                        }
                                    }}
                                />
                                <div className="x-axis" style={(m => ({
                                    marginLeft:m,
                                    width:`calc(100% - ${m}px)`}))(calculateXAxisLeftMargin())}>
                                    <div className="horizontal-line"></div>
                                    {/* {(() => {
                                        const { format, tickValues } = formatXAxis();
                                        const firstTickTs = tickValues[0];
                                        const lastTickTs = tickValues[tickValues.length - 1];
                                        const filteredGappedData = gappedFlattenedData.filter(d => !d.hasOwnProperty(MAX_VALUE_KEY));
                                        const firstDataTs = filteredGappedData[0]?.ts ?? firstTickTs;
                                        const lastDataTs = filteredGappedData.at(-1)?.ts ?? lastTickTs;
                                        const firstTickPercent = (((firstTickTs - firstDataTs)/(lastDataTs - firstDataTs)) * 100);
                                        const lastTickPercent = (((lastDataTs - lastTickTs)/(lastDataTs - firstDataTs)) * 100);
                                        const approxCharWidth = 3;
                                        const labelWidth = format(firstTickTs)?.length * approxCharWidth;
                                        return tickValues.map((ts, index) => {
                                            const isFirst = index === 0;
                                            const isLast = index === tickValues.length - 1;
                                            console.log('First data ts', firstDataTs, 'First tick %', firstTickPercent);

                                            const style = {
                                                marginLeft: isFirst ? `calc(${firstTickPercent}% - ${Math.floor(labelWidth / 2)}px)` : undefined,
                                                marginRight: isLast ? `calc(${lastTickPercent}% - ${Math.floor(labelWidth / 2)}px)` : undefined,
                                                textAlign: 'center'
                                            };
                                            return (
                                                <div key={index} style={style}>
                                                <p>|</p>
                                                {format(ts)}
                                                </div>
                                            );
                                        });
                                    })()} */}
                                    {(() => {
                                        const { format, tickValues } = formatXAxis();

                                        const firstTickTs = tickValues[0];
                                        const lastTickTs  = tickValues[tickValues.length - 1];

                                        const approxCharWidth = 3;
                                        const firstLabelWidth =
                                            format(firstTickTs)?.length * approxCharWidth;

                                        return tickValues.map((ts, index) => {
                                            const isFirst = index === 0;
                                            const isLast  = index === tickValues.length - 1;

                                            const style = {
                                            marginLeft: isFirst
                                                ? `calc(0% - ${Math.floor(firstLabelWidth / 2)}px)`
                                                : undefined,
                                            marginRight: isLast
                                                ? `calc(0% - ${Math.floor(firstLabelWidth / 2)}px)`
                                                : undefined,
                                            textAlign: 'center'
                                            };

                                            return (
                                            <div key={index} style={style}>
                                                <p>|</p>
                                                {format(ts)}
                                            </div>
                                            );
                                        });
                                        })()}
                                </div>
                                <div className="loading-container"
                                    style={{display:loadingData? 'flex':'none'}}>
                                    <div>
                                        <div className="loading-icon"></div>
                                        <p>Loading</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Resizable>
        </div>
    );
};
export default BarChartWidget;